/**
 * The console's static guidance registry — the frontend half of the in-product
 * onboarding contract (see data-models-core-api `docs/onboarding-seen-state-contract.md`).
 *
 * Every unit of guidance has a stable `key` and an integer `version`. The backend
 * (`/me/onboarding`) stores only per-user seen-state keyed by these `key`s; it never
 * sees this registry. Bump a unit's `version` ONLY when the guidance materially changes
 * and users who already saw the old one should see the new one — that is the sole lever
 * that re-surfaces a dismissed/seen unit (see `OnboardingService.shouldShow`).
 */

export type GuidanceType = 'empty' | 'hint' | 'tour' | 'checklist';

export interface GuidanceUnit {
    /** Stable, dot-namespaced id, e.g. `getting-started.checklist`. Never reused for a different unit. */
    readonly key: string;
    /** Starts at 1. Bump to re-show a unit after a material change. */
    readonly version: number;
    readonly type: GuidanceType;
}

/** The getting-started checklist shown in the sidebar foot. */
export const GETTING_STARTED_CHECKLIST = 'getting-started.checklist';

/** First-run hint on the run results view explaining the reconciliation match verdict. */
export const RESULTS_MATCH_VERDICT_HINT = 'results.match-verdict';

/** First-run coachmark tour orienting a new user to the console chrome (nav, checklist, quick search). */
export const APP_ORIENTATION_TOUR = 'app.orientation';

/**
 * All tracked guidance units. `empty`-type units are passive (they render whenever their
 * collection is empty and are not tracked here); only hint/tour/checklist units — which the
 * show-rule and seen-state govern — belong in this registry.
 */
export const ONBOARDING_REGISTRY: Readonly<Record<string, GuidanceUnit>> = {
    [GETTING_STARTED_CHECKLIST]: { key: GETTING_STARTED_CHECKLIST, version: 1, type: 'checklist' },
    [RESULTS_MATCH_VERDICT_HINT]: { key: RESULTS_MATCH_VERDICT_HINT, version: 1, type: 'hint' },
    [APP_ORIENTATION_TOUR]: { key: APP_ORIENTATION_TOUR, version: 1, type: 'tour' },
};
